import { Badge } from '@mantine/core';
import { IconCrown } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { BILLING_ENABLED } from '@constants/features';
import { pageUrl } from '@constants/url';
import { useLanguage } from '@contexts/LanguageContext';
import useSubscription from '@hooks/subscription/use-subscription';
import { deriveTrialStatus } from '@utils/trial-status';

/**
 * Header pill for accounts on a free trial: "Uji Coba · 5 hari" for the whole
 * run, amber inside the warning window, red on the last day and after the
 * trial lapses. Renders nothing for paid plans, while loading, or on error.
 */
export default function TrialBadge() {
  const { t } = useLanguage();
  const { data, loading, error } = useSubscription();

  if (loading || error || !data) return null;
  const trial = deriveTrialStatus(data);
  if (!trial.isTrial) return null;

  const label = trial.ended
    ? t('subscription.trial.header.ended')
    : trial.daysLeft === 0
      ? t('subscription.trial.header.endsToday')
      : t('subscription.trial.header.daysLeft').replace('{n}', String(trial.daysLeft));
  const color = trial.ended || trial.daysLeft === 0 ? 'red' : trial.endsSoon ? 'yellow' : 'blue';

  const common = {
    color,
    variant: 'light' as const,
    size: 'lg',
    leftSection: <IconCrown size={14} />,
    'data-testid': 'trial-badge',
  };

  // Builds without the billing area redirect /subscription to the dashboard
  // (App.tsx), so the pill is a plain status there rather than a dead link.
  return BILLING_ENABLED ? (
    <Badge
      {...common}
      component={Link}
      to={pageUrl.subscription}
      style={{ cursor: 'pointer', textTransform: 'none', flexShrink: 0 }}
    >
      {label}
    </Badge>
  ) : (
    <Badge {...common} style={{ textTransform: 'none', flexShrink: 0 }}>
      {label}
    </Badge>
  );
}
